import React, { Component } from "react";
import { Marked } from "marked";
import { markedHighlight } from "marked-highlight";
import hljs from "highlight.js/lib/common";
import DOMPurify from "dompurify";

import Theme from "context/theme";
import { mermaidConfig } from "./mermaidTheme";
// highlight.js ships NO themeable stylesheet: github.css and github-dark.css both declare bare,
// unscoped `.hljs-keyword {}` rules, so importing both leaves whichever webpack emits last winning
// globally with no selector to toggle. The 15 rules are restated once against the --bh-hl-* family
// in src/vendor.theme.css instead - light is github.css value for value, dark is github-dark.css.
import "./markdown.css";

// Shared Marked instance with syntax highlighting. Used by every widget
// that renders user-authored markdown so the visual treatment is the
// same everywhere (thread widget, message widget, …).
const playgroundMarked = new Marked(
  markedHighlight({
    langPrefix: "hljs language-",
    highlight(code, lang) {
      // A mermaid fence is a DIAGRAM, not source: it must not be highlighted, and must not be
      // HTML-escaped either, since the definition is handed back to mermaid verbatim below.
      if ( "mermaid" === lang ) {
        return code;
      }
      const language = lang && hljs.getLanguage(lang) ? lang : "plaintext";
      try {
        return hljs.highlight(code, { language, ignoreIllegals: true }).value;
      } catch (e) {
        return code;
      }
    }
  })
);

// A ```mermaid fence becomes a placeholder that MarkdownBody fills in after mount. It cannot be
// rendered here: mermaid's API is asynchronous and measures text against a live element. The
// definition rides in a `data-` attribute (DOMPurify keeps those by default) rather than in the
// element's text, so the page never flashes a wall of arrows before the diagram lands.
playgroundMarked.use({
  renderer: {
    code(token) {
      if ( "mermaid" !== token.lang ) {
        return false; // fall through to the highlighter above
      }
      return `<div class="playground-mermaid" data-mermaid="${encodeURIComponent(token.text)}"></div>`;
    }
  }
});

// Mermaid is loaded on demand: it is a large dependency and most markdown carries no diagram, so
// the cost is paid by the first view that actually shows one.
//
// WHAT IS CACHED IS THE MODULE, NOT THE INITIALISATION. `mermaid.initialize` is a module-global side
// effect and `mermaid.render` bakes the palette into the SVG string it returns, so the only way to
// change the palette is to call initialize again — which renderDiagrams does before every pass. It
// is idempotent and cheap. Initialising once here, as this file used to, meant the first diagram on
// the page chose the palette for every diagram after it, for the life of the tab.
let mermaidPromise = null;
function loadMermaid() {
  if ( !mermaidPromise ) {
    mermaidPromise = import("mermaid").then(({ default: mermaid }) => mermaid);
  }
  return mermaidPromise;
}

let mermaidSeq = 0;

/**
 * MarkdownBody — the one markdown renderer, now with mermaid.
 *
 * A diagram that fails to parse falls back to its own source in a `<pre>`, not to an empty box: a
 * broken diagram is usually one somebody needs to read and fix, and hiding the text hides the fix.
 *
 * Props:
 *   text      — the markdown source
 *   className — added to `.playground-markdown`
 *   breaks    — treat a single newline as a line break (see below)
 *
 * **`breaks` is for text somebody TYPED**, and it is off by default because the two kinds of
 * markdown here want opposite things. A document — an `.md` artifact, a plan, a report — is written
 * with hard-wrapped paragraphs, and honouring every newline in one shreds it into ragged lines. A
 * message typed into a field is the other way round: Enter sends and Shift+Enter is a newline, so
 * every newline in it was somebody deliberately starting a new line, and joining two of them into a
 * paragraph loses what they wrote. GitHub draws the same line between a comment box and a file.
 */
export class MarkdownBody extends Component {
  constructor(props) {
    super(props);
    this.ref = React.createRef();
    this.cancelled = false;
  }

  componentDidMount() {
    this.renderDiagrams();
    // A theme change re-renders every diagram in this body. mermaid bakes its palette into the SVG
    // string, so this is a re-render and not a repaint — no stylesheet can reach a drawn diagram.
    // One subscription per mounted body, and there is one body per widget, so this scales with what
    // is actually on screen.
    this.unsubscribeTheme = Theme.subscribe(() => this.renderDiagrams(true));
  }

  componentDidUpdate(previous) {
    if ( previous.text !== this.props.text ) {
      this.renderDiagrams();
    }
  }

  componentWillUnmount() {
    this.cancelled = true;
    if ( this.unsubscribeTheme ) this.unsubscribeTheme();
  }

  /**
   * `force` re-draws diagrams that are ALREADY drawn — the theme-change path. Without it only
   * never-drawn placeholders are picked up, which is what the mount and update paths want.
   *
   * The definition now stays in `data-mermaid` for the life of the node. It used to be removed the
   * moment the diagram was drawn, which meant a theme change had nothing left to re-render FROM;
   * `data-mermaid-drawn` is the flag that used to be "the attribute is gone". Keeping it costs one
   * encoded copy of the source per diagram and is what makes the switch possible at all.
   */
  async renderDiagrams(force) {
    const root = this.ref.current;
    if ( !root ) return;
    const selector = force
      ? ".playground-mermaid[data-mermaid]"
      : ".playground-mermaid[data-mermaid]:not([data-mermaid-drawn])";
    const nodes = Array.from(root.querySelectorAll(selector));
    if ( 0 === nodes.length ) return;

    let mermaid;
    try {
      mermaid = await loadMermaid();
      // Re-initialised on every pass, because the palette is a module global inside mermaid and this
      // is the only way to change it. It must happen before the renders below, not once at load.
      mermaid.initialize(mermaidConfig(Theme.resolved()));
    } catch (e) {
      return; // unavailable — the placeholders stay empty rather than taking the page down
    }
    if ( this.cancelled ) return;

    for ( const node of nodes ) {
      const definition = decodeURIComponent(node.getAttribute("data-mermaid") || "");
      node.classList.remove("playground-mermaid-failed");
      try {
        const { svg } = await mermaid.render(`bh-mermaid-${++mermaidSeq}`, definition);
        if ( this.cancelled ) return;
        node.innerHTML = svg;
        node.setAttribute("data-mermaid-drawn", "1");
      } catch (e) {
        if ( this.cancelled ) return;
        node.classList.add("playground-mermaid-failed");
        node.setAttribute("data-mermaid-drawn", "1");
        const pre = document.createElement("pre");
        pre.textContent = definition;
        node.innerHTML = "";
        node.appendChild(pre);
      }
    }
  }

  render() {
    const { text, className, breaks } = this.props;
    if ( typeof text !== "string" ) {
      return null;
    }
    const cls = className ? `playground-markdown ${className}` : "playground-markdown";
    // Per call, not per instance: the same renderer serves documents and typed messages, and the
    // option does not leak from one parse to the next.
    const html = { __html: DOMPurify.sanitize(playgroundMarked.parse(text, { breaks: !!breaks })) };
    return <div ref={this.ref} className={cls} dangerouslySetInnerHTML={html} />;
  }
}

export default MarkdownBody;
